import React from "react";
import { useSetting } from "./SettingsStore.tsx";
import { ProblemFormatOptions, showsOperatorBar } from "../Constants.ts";
import { labelFor } from "../Labels.ts";
import { usesValueRanges } from "./SettingsContainer.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface Chip {
    /** The rail section the chip opens the sheet at. */
    anchor: string;
    label: string;
}

/**
 * The row of chips under the header on a narrow screen (ADR 0061 §7): what
 * the sheet is, at a glance, with each chip opening the settings sheet
 * scrolled to the section that changes it. Hidden at 900px and wider, where the
 * rail itself is on screen.
 *
 * The first chip carries the accent fill: it names the operation (or the
 * format, where there is no operator bar), which is what the page is about.
 */
export function ChipStrip({ onOpen }: { onOpen: (anchor: string) => void }) {
    const settings = useSetting((s) => ({
        Format: s.Format,
        Operators: (s.Operators ?? []).map((o) => ({ value: o.value })),
        Language: s.Language,
        NumberOfDigits: s.NumberRanges[0].NumberOfDigits,
        NumberOfProblemsPerPage: s.NumberOfProblemsPerPage,
        NumberOfPages: s.NumberOfPages,
    }));
    const t = useTranslation();

    const chips: Chip[] = [];
    if (showsOperatorBar(settings.Format)) {
        const operators = settings.Operators;
        chips.push({
            anchor: 'rail-operation',
            label: operators.length
                ? operators.map((o) => labelFor(o.value, settings.Language)).join(' + ')
                : t('workbench.operation'),
        });
    }
    chips.push({ anchor: 'rail-format', label: labelFor(settings.Format, settings.Language) });
    if (usesValueRanges(settings.Format)) {
        const digits = settings.NumberOfDigits;
        chips.push({
            anchor: 'rail-first',
            label: digits === 1 ? t('workbench.oneDigitChip') : format(t('workbench.digitsChip'), { n: digits }),
        });
    }
    if (settings.Format !== ProblemFormatOptions.SpiralReview) {
        chips.push({
            anchor: 'rail-paper',
            label: format(t('workbench.perPageChip'), { n: settings.NumberOfProblemsPerPage }),
        });
    }
    chips.push({
        anchor: 'rail-paper',
        label: settings.NumberOfPages === 1
            ? t('workbench.onePage')
            : format(t('workbench.pages'), { n: settings.NumberOfPages }),
    });

    return (
        <div className="chipStrip" role="group" aria-label={t('workbench.quickSettingsAria')}>
            {chips.map((chip, index) => (
                <button
                    key={index}
                    type="button"
                    className={'chip' + (index === 0 ? ' chipOn' : '')}
                    aria-controls="worksheetSettings"
                    aria-haspopup="dialog"
                    onClick={() => onOpen(chip.anchor)}
                >
                    {chip.label}
                    <span className="chipCaret" aria-hidden="true">▾</span>
                </button>
            ))}
        </div>
    );
}

export default ChipStrip;
